import { compose, find, identity, is, prop, propEq } from 'ramda'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { Absolute, Relative } from '../helpers/Position'
import { useControlledDropdown } from '../../utils/helpers/controlledDropdown'
import { useLazyOptions } from '../../utils/helpers/options'
import { useOnStopTypingState } from '../../utils/helpers/onStopTyping'
import Input from './Input'
import LabelIconListItem from '../list/LabelIconListItem'
import Loading from '../helpers/Loading'
import Panel from '../structure/Panel'
import Picker from './picker/Picker'
import Separator from '../helpers/Separator'
import Text from '../presentation/Text'

const Select = ({
  t,
  placeholder,
  value,
  disabled,
  onChange,
  onCreate,
  header,
  idField,
  labelField,
  descriptionField,
  tooltipField,
  options,
  selectedLabel,
  multiple,
  selectOnlyId,
  disableEmptyOption,
  mergedOptions,
  onClose,
  disablePagination,
  disableItem,
  reasonForDisablePresenter,
  selectAll,
  selectAllLabel,
  onSelectAll,
  useEmptyOption,
  hasSelectAll,
  ...props
}) => {
  t = t || identity
  const wrapperRef = React.useRef()
  const [search, finalSearch, setSearch] = useOnStopTypingState('')
  let [{ localOptions, loading, canLoadMore }, fetchOptions, fetchMoreOptions] = useLazyOptions(options, {
    disablePagination
  })

  const { t: tCommon } = useTranslation('common')

  if (selectAll && !search && localOptions && localOptions.length > 0) {
    const selectAllOption = {
      [idField || 'id']: 'select-all',
      [labelField || 'name']: selectAllLabel || tCommon('selectAll')
    }

    if (descriptionField) {
      if (Array.isArray(descriptionField)) {
        let currentObj = selectAllOption
        for (let i = 0; i < descriptionField.length - 1; i++) {
          currentObj[descriptionField[i]] = {}
          currentObj = currentObj[descriptionField[i]]
        }
        currentObj[descriptionField[descriptionField.length - 1]] = ''
      } else {
        selectAllOption[descriptionField] = ''
      }
    }

    if (!localOptions.some(option => option[idField || 'id'] === 'select-all')) {
      localOptions = [selectAllOption, ...localOptions]
    }
  }

  localOptions = [...(!search ? mergedOptions || [] : []), ...(localOptions || [])]

  idField = idField || 'id'
  labelField = labelField || 'name'
  descriptionField = descriptionField || 'description'
  tooltipField = tooltipField || 'fullName'

  const {
    handleClose,
    setOpen,
    open,
    current,
    setCurrent,
    onKeyDown,
    handleChange: originalHandleChange
  } = useControlledDropdown({
    wrapperRef,
    value,
    options: localOptions,
    multiple,
    onChange,
    idField,
    onClose
  })

  const handleChange = selectedValue => {
    if (selectAll && selectedValue && selectedValue[idField] === 'select-all') {
      if (onSelectAll) {
        onSelectAll()
      }
    } else {
      originalHandleChange(selectedValue)
    }
  }

  const isValueObject = is(Object, value)
  if (!selectedLabel && is(Array, options) && !isValueObject) {
    selectedLabel = compose(prop(labelField), find(propEq(idField, value)))(options)
  }
  if (!selectedLabel) selectedLabel = value
  if (!!selectedLabel?.length && selectedLabel?.map) {
    selectedLabel = selectedLabel.map(compose(t, prop(labelField))).join(', ')
  } else if (is(Object, selectedLabel)) {
    selectedLabel = t(prop(labelField, selectedLabel))
  } else selectedLabel = t(selectedLabel)

  React.useEffect(() => {
    if (open) {
      fetchOptions(finalSearch)
      setCurrent(0)
    }
  }, [finalSearch, open])

  return (
    <div className="select" ref={wrapperRef}>
      <Input
        placeholder={placeholder}
        value={!open ? selectedLabel : search}
        onChange={setSearch}
        iconRight={!open ? 'BsCaretDown' : 'BsCaretUp'}
        disabled={disabled}
        onKeyPress={e => {
          e.key === 'Enter' && e.preventDefault()
        }}
        onKeyDown={onKeyDown}
        autoComplete="new-password"
        {...props}
        onFocus={e => {
          setOpen(true)
          props.onFocus?.(e)
        }}
      />
      <Relative zIndex={1000}>
        {open && (
          <Absolute top={0} left={0} right={0} zIndex={1000}>
            <Panel
              padding="0"
              radius={0.3}
              borderColor="light3"
              shadow="dark4"
              maxHeight={16}
              scroll
              minHeight={2.5}
              onScrollToBottom={() => fetchMoreOptions(finalSearch)}
            >
              {!loading && !localOptions?.length && (
                <Separator weight={2}>
                  <Text color="dark5">Nenhum item encontrado</Text>
                </Separator>
              )}
              <Picker
                t={t}
                currentIndex={current}
                useEmptyOption={useEmptyOption !== undefined ? useEmptyOption : !disableEmptyOption && !finalSearch}
                options={localOptions}
                onChange={handleChange}
                idField={idField}
                labelField={labelField}
                descriptionField={descriptionField}
                tooltipField={tooltipField}
                value={value}
                multiple={multiple}
                selectOnlyId={selectOnlyId}
                hasSelectAll={hasSelectAll}
                onSelectAll={onSelectAll}
                header={
                  !finalSearch
                    ? header
                    : !!onCreate && (
                        <LabelIconListItem
                          label={`Criar novo: ${finalSearch}`}
                          icon="RiCheckboxCircleLine"
                          iconColor="white"
                          color="white"
                          bgColor="purple"
                          onClick={() => {
                            onCreate(search)
                            handleClose()
                          }}
                        />
                      )
                }
                disableItem={disableItem}
                reasonForDisablePresenter={reasonForDisablePresenter}
              />
              {canLoadMore && <Loading size="small" minHeight={2.5} />}
            </Panel>
          </Absolute>
        )}
      </Relative>
    </div>
  )
}

export default Select
